import React, { useMemo, useState, useRef, useEffect } from 'react'
import { Box } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Widget from '@/components/common/Widget'
import { NotificationStats } from '@/mockdata/notifications'
import { getOpenNotifications } from '@/services/consumerService'
import NotificationStatsList from './NotificationStatsList'
import Link from '@assets/NotificationContainerProps/Link.svg'
import { useNavigate, useParams } from 'react-router-dom'
import { useGeneralNotificationsStore } from '@/store/notification/general'
import { useAppTheme } from '@/theme/ThemeContext'

export interface NotificationContainerProps {
  title?: string
  height?: string | number
  width?: string | number
  maxHeight?: string | number
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({
  title = 'התראות',
  height = 'fit-content',
  width = 'fit-content',
  maxHeight = '310px',
}) => {
  const [labels, setLabels] = useState<{ label: string; action: string }[]>([
    { label: 'עריכת סדר ההתראות', action: 'edit_order' },
    { label: 'מעבר לכל ההתראות', action: 'view_all' },
    { label: 'הגדרות והעדפות', action: 'settings' },
  ])
  const [items, setItems] = useState<NotificationStats[]>([])
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { siteId } = useParams()
  const accountId = localStorage.getItem('accountId') || '46'

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      const result = await getOpenNotifications(accountId)
      const mapped: NotificationStats[] = result.map(n => ({
        id: n.notificationTypeId.toString(),
        title: n.notificationTypeName,
        count: n.count,
        total: 0,
        color: '#E3F5FF',
      }))
      setItems(mapped)
    }
    fetchNotifications()
  }, [accountId])
  const { setFilteredGeneralNotifications, filteredGeneralNotifications } =
    useGeneralNotificationsStore()

  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  // Theme-aware colors for scroll indicator
  const scrollColors = {
    gradientStart: isDarkMode ? 'rgba(29, 50, 90, 0)' : 'rgba(248, 249, 250, 0)',
    gradientEnd: isDarkMode ? 'rgba(29, 50, 90, 0.8)' : 'rgba(248, 249, 250, 0.8)',
    iconColor: isDarkMode ? '#94A3B899' : '#003F6199',
  }
  const filtered = useMemo(() => {
    return items
  }, [items])

  const handleItemClick = (item: NotificationStats) => {
    const filteredData = [...filteredGeneralNotifications, item]
    filteredData[0] = item
    setFilteredGeneralNotifications(filteredData)
    navigate(`/${siteId}/notifications`)
  }

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } =
          scrollContainerRef.current
        const isScrollable = scrollHeight > clientHeight
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 5
        setShowScrollIndicator(isScrollable && !isAtBottom)
      }
    }

    const timeoutId = setTimeout(checkScrollable, 100)

    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollable)
      window.addEventListener('resize', checkScrollable)

      checkScrollable()

      return () => {
        clearTimeout(timeoutId)
        container.removeEventListener('scroll', checkScrollable)
        window.removeEventListener('resize', checkScrollable)
      }
    }

    return () => clearTimeout(timeoutId)
  }, [items])

  const handleScrollDown = () => {
    if (scrollContainerRef.current) {
      const { clientHeight } = scrollContainerRef.current
      scrollContainerRef.current.scrollBy({
        top: clientHeight,
        behavior: 'smooth',
      })
    }
  }

  const handleMenuAction = (action: string) => {
    switch (action) {
      case 'view_all':
        navigate(`/${siteId}/notifications`)
        break
      case 'refresh':
        getOpenNotifications(accountId).then(result => {
          setItems(result.map(n => ({
            id: n.notificationTypeId.toString(),
            title: n.notificationTypeName,
            count: n.count,
            total: 0,
            color: '#E3F5FF',
          })))
        })
        break
      case 'edit_order':
        setIsEditMode(!isEditMode)
        setLabels(prev => [
          {
            ...prev[0],
            label: !isEditMode ? 'שמירת סדר התראות' : 'עריכת סדר ההתראות',
          },
          ...prev.slice(1),
        ])
        break
      case 'settings':
        navigate(`/${siteId}/notifications/manage-notifications`)
        break
      case 'export':
        break
      default:
        break
    }
  }

  const handleReorderItems = (startIndex: number, endIndex: number) => {
    const result = Array.from(items)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    setItems(result)
  }

  return (
    <Widget
      title={title}
      height={height}
      width={width}
      maxHeight={maxHeight}
      expandIcon={Link}
      expandIconSize={15}
      expandHref={`/${siteId}/notifications`}
      onMenuAction={handleMenuAction}
      menuItems={labels}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Content - Notification List */}
        <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <NotificationStatsList
            ref={scrollContainerRef}
            items={filtered}
            onItemClick={handleItemClick}
            maxHeight={maxHeight}
            isEditMode={isEditMode}
            onReorder={handleReorderItems}
          />

          {showScrollIndicator && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '0px',
                background:
                  `linear-gradient(to bottom, ${scrollColors.gradientStart} 0%, ${scrollColors.gradientEnd} 100%)`,
              }}
            />
          )}
        </Box>

        {showScrollIndicator && (
          <Box
            onClick={handleScrollDown}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: '42px',
                color: scrollColors.iconColor,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(2px)',
                  opacity: 0.7,
                },
                '&:active': {
                  transform: 'translateY(4px)',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Widget>
  )
}

export default NotificationContainer
